import * as XLSX from 'xlsx';

export interface PrintableOrderLine {
  name:      string;
  sku:       string;
  imageUrl:  string | null;
  requested: number;
  approved:  number;
  unitPrice: number;
  lineTotal: number;
  discount?: number;
}

export interface PrintableOrder {
  id:          string;
  shortId:     string;
  statusLabel: string;
  isOpen:      boolean;
  createdAt:   string;
  clientName:  string;
  clientEmail: string | null;
  clientPhone: string | null;
  vatRate:     number;
  discountReason?: string | null;
  lines:       PrintableOrderLine[];
}

export interface PrintableReturnLine {
  name:      string;
  sku:       string;
  requested: number;
  received:  number | null;
  unitPrice: number;
  reason:    string | null;
}

export interface PrintableReturn {
  id:          string;
  shortId:     string;
  statusLabel: string;
  isOpen:      boolean;
  createdAt:   string;
  clientName:  string;
  reason:      string | null;
  adminNotes:  string | null;
  lines:       PrintableReturnLine[];
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export const fmtDocDate = (iso: string) =>
  new Date(iso).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });

export function orderTotals(order: PrintableOrder) {
  const subtotal = order.lines.reduce((s, l) => s + l.lineTotal, 0);
  const discount = order.lines.reduce((s, l) => s + (l.discount ?? 0), 0);
  const vat      = subtotal * (order.vatRate / 100);
  return {
    subtotal: round2(subtotal), vat: round2(vat), total: round2(subtotal + vat),
    discount: round2(discount), gross: round2(subtotal + discount),
  };
}

export const orderHasDiscount = (order: PrintableOrder) => order.lines.some(l => (l.discount ?? 0) > 0);

const safeName = (s: string) => s.replace(/[\\/:*?"<>|]+/g, '').trim().slice(0, 60);

export function orderFileBase(orders: PrintableOrder[], prefix: string) {
  const [only] = orders;
  if (orders.length === 1 && only) return safeName(`${prefix} ${only.shortId} ${only.clientName}`);
  return safeName(`${prefix} ${orders.length} - ${fmtDocDate(new Date().toISOString()).replace(/\./g, '-')}`);
}

export function returnFileBase(returns: PrintableReturn[], prefix: string) {
  const [only] = returns;
  if (returns.length === 1 && only) return safeName(`${prefix} ${only.shortId} ${only.clientName}`);
  return safeName(`${prefix} ${returns.length} - ${fmtDocDate(new Date().toISOString()).replace(/\./g, '-')}`);
}

function writeWorkbook(sheets: { name: string; rows: (string | number | null)[][] }[], fileBase: string) {
  const wb = XLSX.utils.book_new();
  wb.Workbook = { Views: [{ RTL: true }] };
  for (const s of sheets) {
    const ws = XLSX.utils.aoa_to_sheet(s.rows);
    const widths = s.rows[0]?.map((_, i) =>
      Math.min(40, Math.max(10, ...s.rows.map(r => String(r[i] ?? '').length + 2))),
    ) ?? [];
    ws['!cols'] = widths.map(wch => ({ wch }));
    XLSX.utils.book_append_sheet(wb, ws, s.name);
  }
  XLSX.writeFile(wb, `${fileBase}.xlsx`);
}

export function exportOrdersToExcel(orders: PrintableOrder[], fileBase: string) {
  const header = ['מס׳ הזמנה', 'לקוח', 'טלפון', 'אימייל', 'תאריך', 'סטטוס',
    'מק״ט', 'מוצר', 'כמות שהוזמנה', 'כמות מאושרת', 'מחיר ליחידה', 'סה״כ שורה'];
  const withDiscount = orders.some(orderHasDiscount);
  if (withDiscount) header.splice(11, 0, 'הנחה');
  const rows: (string | number | null)[][] = [header];
  for (const o of orders) {
    for (const l of o.lines) {
      const row: (string | number | null)[] = [o.shortId, o.clientName, o.clientPhone ?? '', o.clientEmail ?? '', fmtDocDate(o.createdAt),
        o.statusLabel, l.sku, l.name, l.requested, l.approved, round2(l.unitPrice), round2(l.lineTotal)];
      if (withDiscount) row.splice(11, 0, round2(l.discount ?? 0));
      rows.push(row);
    }
  }

  const pad = (label: string, value: number | string) => {
    const r: (string | number | null)[] = Array(header.length).fill(null);
    r[0] = label; r[header.length - 1] = value;
    return r;
  };

  const [only] = orders;
  if (orders.length === 1 && only) {
    const t = orderTotals(only);
    rows.push([]);
    if (t.discount > 0) {
      rows.push(pad('סה״כ לפני הנחה', t.gross));
      rows.push(pad('הנחה', -t.discount));
    }
    rows.push(pad('סה״כ לפני מע״מ', t.subtotal));
    rows.push(pad(`מע״מ (${only.vatRate}%)`, t.vat));
    rows.push(pad('סה״כ כולל מע״מ', t.total));
    if (only.discountReason) rows.push(pad('סיבת הנחה', only.discountReason));
    writeWorkbook([{ name: 'הזמנה', rows }], fileBase);
    return;
  }

  const summary: (string | number | null)[][] = [[
    'מס׳ הזמנה', 'לקוח', 'תאריך', 'סטטוס', 'פריטים', 'יחידות מאושרות',
    'סה״כ לפני מע״מ', 'מע״מ', 'סה״כ כולל מע״מ',
  ]];
  for (const o of orders) {
    const t = orderTotals(o);
    summary.push([o.shortId, o.clientName, fmtDocDate(o.createdAt), o.statusLabel, o.lines.length,
      o.lines.reduce((s, l) => s + l.approved, 0), t.subtotal, t.vat, t.total]);
  }
  writeWorkbook([{ name: 'שורות', rows }, { name: 'סיכום הזמנות', rows: summary }], fileBase);
}

export function exportReturnsToExcel(returns: PrintableReturn[], fileBase: string) {
  const rows: (string | number | null)[][] = [[
    'מס׳ החזרה', 'לקוח', 'תאריך', 'סטטוס', 'סיבת החזרה', 'מק״ט', 'מוצר',
    'כמות מבוקשת', 'כמות שהתקבלה', 'מחיר ליחידה', 'סיבה לשורה',
  ]];
  for (const r of returns) {
    for (const l of r.lines) {
      rows.push([r.shortId, r.clientName, fmtDocDate(r.createdAt), r.statusLabel, r.reason ?? '',
        l.sku, l.name, l.requested, l.received ?? '', round2(l.unitPrice), l.reason ?? '']);
    }
  }
  writeWorkbook([{ name: 'החזרות', rows }], fileBase);
}
